'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';

interface ForgotPasswordClientProps {
  locale: string;
}

type Step = 'phone' | 'sms' | 'password' | 'success';

interface FormData {
  phoneNumber: string;
  smsCode: string;
  newPassword: string;
  confirmPassword: string;
}

export default function ForgotPasswordClient({ locale }: ForgotPasswordClientProps) {
  const t = useTranslations('forgotPassword');
  const loginT = useTranslations('login');
  const router = useRouter();
  
  const [currentStep, setCurrentStep] = useState<Step>('phone');
  const [formData, setFormData] = useState<FormData>({
    phoneNumber: '',
    smsCode: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [phoneError, setPhoneError] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    hasNumber: false,
    hasLowercase: false,
    hasUppercase: false,
    hasSpecialChar: false
  });

  // Phone number validation
  const validatePhoneNumber = (phone: string): boolean => {
    // 5xx xxx xx xx formatında olmalı
    const phoneRegex = /^5\d{2}\s\d{3}\s\d{2}\s\d{2}$/;
    return phoneRegex.test(phone);
  };

  // Handle phone number change with formatting
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    // Sadece rakam karakterlerine izin ver
    value = value.replace(/[^\d]/g, '');
    
    // Türkiye ülke kodu varsa kaldır (90)
    if (value.startsWith('90') && value.length > 10) {
      value = value.substring(2);
    }
    
    // Başındaki 0'ı kaldır
    if (value.startsWith('0')) {
      value = value.substring(1);
    }
    
    // Maksimum uzunluk kontrolü (5xx xxx xx xx = 10 rakam)
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    // Format: 5xx xxx xx xx
    let formattedValue = '';
    if (value.length > 0) {
      formattedValue = value.substring(0, 3);
    }
    if (value.length > 3) {
      formattedValue += ' ' + value.substring(3, 6);
    }
    if (value.length > 6) {
      formattedValue += ' ' + value.substring(6, 8);
    }
    if (value.length > 8) {
      formattedValue += ' ' + value.substring(8, 10);
    }
    
    setFormData(prev => ({ ...prev, phoneNumber: formattedValue }));
    
    if (formattedValue && !validatePhoneNumber(formattedValue)) {
      setPhoneError(t('phoneStep.phoneError'));
    } else {
      setPhoneError('');
    }
  };

  // SMS code validation
  const validateSmsCode = (code: string): boolean => {
    return /^\d{6}$/.test(code);
  };

  // Password validation
  const validatePassword = (password: string): boolean => {
    return password.length >= 8;
  };

  // Password requirements validation
  const validatePasswordRequirements = (password: string) => {
    const requirements = {
      minLength: password.length >= 8,
      hasNumber: /\d/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
    setPasswordRequirements(requirements);
    return Object.values(requirements).every(Boolean);
  };

  // Handle phone number submission
  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setErrors([]);
    
    if (!validatePhoneNumber(formData.phoneNumber)) {
      setPhoneError(t('phoneStep.phoneError'));
      return;
    }

    setLoading(true);
    try {
      // Remove spaces from phone number
      const phoneNumber = formData.phoneNumber.replace(/\s/g, '');
      
      // SMS kodu gönderme API'si
      const response = await fetch('/api/smscode/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: phoneNumber,
          codeType: 1, // SmsCodeType.ForgotPassword
        }),
      });

      const data = await response.json();
      
      if (data.isSuccess) {
        setCurrentStep('sms');
        // İlk SMS gönderildiğinde timer başlat
        setResendCooldown(120);
        const interval = setInterval(() => {
          setResendCooldown(prev => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          setErrors(data.errors);
        } else {
          setError(data.message || t('phoneStep.sendError'));
        }
      }
    } catch (err) {
      setError(t('phoneStep.sendError'));
    } finally {
      setLoading(false);
    }
  };

  // Handle SMS verification
  const handleSmsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setErrors([]);
    
    if (!validateSmsCode(formData.smsCode)) {
      setError(t('smsStep.invalidCode'));
      return;
    }

    setLoading(true);
    try {
      // Remove spaces from phone number
      const phoneNumber = formData.phoneNumber.replace(/\s/g, '');
      
      // SMS doğrulama API'si
      const response = await fetch('/api/smscode/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: phoneNumber,
          code: formData.smsCode,
          codeType: 1, // SmsCodeType.ForgotPassword
        }),
      });

      const data = await response.json();
      
      if (data.isSuccess) {
        setCurrentStep('password');
      } else {
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          setErrors(data.errors);
        } else {
          setError(data.message || t('smsStep.wrongCode'));
        }
      }
    } catch (err) {
      setError(t('smsStep.networkError'));
    } finally {
      setLoading(false);
    }
  };

  // Handle password reset
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setErrors([]);
    
    if (!validatePasswordRequirements(formData.newPassword)) {
      setError(t('passwordStep.passwordError'));
      return;
    }
    
    if (formData.newPassword !== formData.confirmPassword) {
      setError(t('passwordStep.passwordMismatch'));
      return;
    }

    setLoading(true);
    try {
      // Remove spaces from phone number
      const phoneNumber = formData.phoneNumber.replace(/\s/g, '');
      
      const response = await fetch('/api/auth/forgot-password/reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: phoneNumber,
          smsCode: formData.smsCode,
          newPassword: formData.newPassword,
          confirmPassword: formData.confirmPassword,
        }),
      });

      const data = await response.json();
      
      if (data.isSuccess) {
        setCurrentStep('success');
      } else {
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          setErrors(data.errors);
        } else {
          setError(data.message || t('passwordStep.resetError'));
        }
      }
    } catch (err) {
      setError(t('passwordStep.serverError'));
    } finally {
      setLoading(false);
    }
  };

  // Handle resend SMS code
  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    
    setLoading(true);
    setError('');
    setErrors([]);
    try {
      // Remove spaces from phone number and add +90 prefix for API
      const phoneNumber = '+90' + formData.phoneNumber.replace(/\s/g, '');
      
      // SMS kodu tekrar gönderme API'si
      const response = await fetch('/api/smscode/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: phoneNumber,
        }),
      });

      const data = await response.json();
      
      if (data.isSuccess) {
        setResendCooldown(120);
        const interval = setInterval(() => {
          setResendCooldown(prev => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          setErrors(data.errors);
        } else {
          setError(data.message || t('smsStep.resendError'));
        }
      }
    } catch (err) {
      setError(t('smsStep.resendError'));
    } finally {
      setLoading(false);
    }
  };

  // Render phone step
  const renderPhoneStep = () => (
    <form onSubmit={handlePhoneSubmit} className="space-y-4 sm:space-y-6">
      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
          {t('phoneStep.phone')} <span className="text-red-500">{t('phoneStep.required')}</span>
        </label>
        <div className="relative">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
            <svg width="16" height="16" className="sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 16.92V19.92C22.0011 20.1985 21.9441 20.4742 21.8325 20.7294C21.7209 20.9846 21.5573 21.2136 21.3521 21.4019C21.1469 21.5902 20.9046 21.7335 20.6407 21.8227C20.3769 21.9119 20.0973 21.9452 19.82 21.92C16.7428 21.5856 13.787 20.5341 11.19 18.85C8.77382 17.3146 6.72533 15.2661 5.18999 12.85C3.49997 10.2412 2.44824 7.27099 2.11999 4.18C2.09477 3.90347 2.12787 3.62476 2.21649 3.36162C2.30512 3.09849 2.44756 2.85669 2.63476 2.65171C2.82196 2.44673 3.04971 2.28291 3.30351 2.17052C3.55731 2.05813 3.83166 2.00026 4.10999 2H7.10999C7.59522 1.99522 8.06577 2.16708 8.43376 2.48353C8.80175 2.79999 9.042 3.23945 9.10999 3.72C9.23662 4.68007 9.47144 5.62273 9.80999 6.53C9.94454 6.88792 9.97348 7.27675 9.89381 7.64958C9.81415 8.02242 9.62976 8.36256 9.35999 8.63L8.08999 9.9C9.51355 12.4136 11.5864 14.4865 14.1 15.91L15.37 14.64C15.6374 14.3702 15.9776 14.1858 16.3504 14.1062C16.7233 14.0265 17.1121 14.0555 17.47 14.19C18.3773 14.5286 19.3199 14.7634 20.28 14.89C20.7658 14.9585 21.2094 15.2032 21.5265 15.5775C21.8437 15.9518 22.0122 16.4296 22 16.92Z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="absolute left-8 sm:left-10 top-1/2 transform -translate-y-1/2 z-10 text-gray-500 font-medium text-sm sm:text-base">
            +90
          </div>
          <input
            type="tel"
            id="phone"
            name="phone"
            autoComplete="tel-national"
            value={formData.phoneNumber}
            onChange={handlePhoneChange}
            placeholder="5xx xxx xx xx"
            maxLength={19}
            className={`w-full pl-16 sm:pl-20 pr-4 py-3 sm:py-4 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent shadow-sm text-sm sm:text-base ${
              phoneError ? 'border-red-500' : 'border-gray-300'
            }`}
            required
          />
        </div>
        {phoneError && (
          <p className="text-red-500 text-xs sm:text-sm mt-1">{phoneError}</p>
        )}
      </div>
      
      {(error || errors.length > 0) && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 sm:p-4">
          {error && (
            <p className="text-red-600 text-xs sm:text-sm mb-2">{error}</p>
          )}
          {errors.length > 0 && (
            <div>
              {errors.map((err: string, index: number) => (
                <p key={index} className="text-red-600 text-xs sm:text-sm">{err}</p>
              ))}
            </div>
          )}
        </div>
      )}
      
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-red-600 text-white py-3 sm:py-4 px-4 rounded-md font-medium text-sm sm:text-base md:text-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? t('phoneStep.sending') : t('phoneStep.sendCode')}
      </button>
      
      <div className="text-center">
        <Link href={`/${locale}/giris`} className="text-xs sm:text-sm font-medium text-red-600 hover:text-red-500">
          {t('backToLogin')}
        </Link>
      </div>
    </form>
  );

  // Render SMS step
  const renderSmsStep = () => (
    <form onSubmit={handleSmsSubmit} className="space-y-4 sm:space-y-6">
      <div className="text-center mb-3 sm:mb-4">
        <p className="text-xs sm:text-sm text-gray-600">
          {t('smsStep.sentTo')} <span className="font-medium">{formData.phoneNumber}</span>
        </p>
      </div>
      
      <div>
        <label htmlFor="smsCode" className="block text-sm font-medium text-gray-700 mb-1">
          {t('smsStep.enterCode')}
        </label>
        <input
          type="text"
          id="smsCode"
          value={formData.smsCode}
          onChange={(e) => setFormData(prev => ({ ...prev, smsCode: e.target.value }))}
          placeholder={t('smsStep.codePlaceholder')}
          maxLength={6}
          className="w-full px-4 py-3 sm:py-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent shadow-sm text-sm sm:text-base text-center text-lg tracking-widest"
          required
        />
      </div>
      
      {(error || errors.length > 0) && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 sm:p-4">
          {error && (
            <p className="text-red-600 text-xs sm:text-sm mb-2">{error}</p>
          )}
          {errors.length > 0 && (
            <div>
              {errors.map((err: string, index: number) => (
                <p key={index} className="text-red-600 text-xs sm:text-sm">{err}</p>
              ))}
            </div>
          )}
        </div>
      )}
      
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-red-600 text-white py-3 sm:py-4 px-4 rounded-md font-medium text-sm sm:text-base md:text-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? t('smsStep.verifying') : t('smsStep.verify')}
      </button>
      
      <div className="text-center">
        <p className="text-xs sm:text-sm text-gray-600 mb-2">{t('smsStep.didntReceive')}</p>
        <button
          type="button"
          onClick={handleResendCode}
          disabled={loading || resendCooldown > 0}
          className="text-xs sm:text-sm font-medium text-red-600 hover:text-red-500 disabled:opacity-50"
        >
          {resendCooldown > 0 
            ? `${t('smsStep.resendIn')} ${resendCooldown}s`
            : t('smsStep.resendCode')
          }
        </button>
      </div>
    </form>
  );

  // Render password step
  const renderPasswordStep = () => (
    <form onSubmit={handlePasswordSubmit} className="space-y-4 sm:space-y-6">
      <div>
        <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
          {t('passwordStep.newPassword')} <span className="text-red-500">{t('passwordStep.required')}</span>
        </label>
        <div className="relative">
          <input
            type={showNewPassword ? "text" : "password"}
            id="newPassword"
            value={formData.newPassword}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, newPassword: e.target.value }));
              validatePasswordRequirements(e.target.value);
            }}
            placeholder={t('passwordStep.newPasswordPlaceholder')}
            className="w-full px-10 py-3 sm:py-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent shadow-sm text-sm sm:text-base"
            required
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <svg width="16" height="16" className="sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="#9CA3AF" strokeWidth="2"/>
              <circle cx="12" cy="16" r="1" fill="#9CA3AF"/>
              <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="#9CA3AF" strokeWidth="2"/>
            </svg>
          </div>
          <button
            type="button"
            onClick={() => setShowNewPassword(!showNewPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            {showNewPassword ? (
              <svg width="16" height="16" className="sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg width="16" height="16" className="sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 12C2 12 5.63636 6 12 6C18.3636 6 22 12 22 12C22 12 18.3636 18 12 18C5.63636 18 2 12 2 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 2L22 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
        </div>
        
        {/* Şifre Gereksinimleri */}
        {formData.newPassword && (
          <div className="mt-3 bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
            <p className="text-xs sm:text-sm font-medium text-gray-700 mb-2">
              {t('passwordStep.passwordRequirements.title')}
            </p>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                  passwordRequirements.minLength ? 'bg-green-500' : 'bg-gray-300'
                }`}>
                  {passwordRequirements.minLength && (
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <span className={`text-xs sm:text-sm ${
                  passwordRequirements.minLength ? 'text-green-700 font-medium' : 'text-gray-600'
                }`}>
                  {t('passwordStep.passwordRequirements.minLength')}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                  passwordRequirements.hasNumber ? 'bg-green-500' : 'bg-gray-300'
                }`}>
                  {passwordRequirements.hasNumber && (
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <span className={`text-xs sm:text-sm ${
                  passwordRequirements.hasNumber ? 'text-green-700 font-medium' : 'text-gray-600'
                }`}>
                  {t('passwordStep.passwordRequirements.hasNumber')}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                  passwordRequirements.hasLowercase ? 'bg-green-500' : 'bg-gray-300'
                }`}>
                  {passwordRequirements.hasLowercase && (
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <span className={`text-xs sm:text-sm ${
                  passwordRequirements.hasLowercase ? 'text-green-700 font-medium' : 'text-gray-600'
                }`}>
                  {t('passwordStep.passwordRequirements.hasLowercase')}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                  passwordRequirements.hasUppercase ? 'bg-green-500' : 'bg-gray-300'
                }`}>
                  {passwordRequirements.hasUppercase && (
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <span className={`text-xs sm:text-sm ${
                  passwordRequirements.hasUppercase ? 'text-green-700 font-medium' : 'text-gray-600'
                }`}>
                  {t('passwordStep.passwordRequirements.hasUppercase')}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                  passwordRequirements.hasSpecialChar ? 'bg-green-500' : 'bg-gray-300'
                }`}>
                  {passwordRequirements.hasSpecialChar && (
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <span className={`text-xs sm:text-sm ${
                  passwordRequirements.hasSpecialChar ? 'text-green-700 font-medium' : 'text-gray-600'
                }`}>
                  {t('passwordStep.passwordRequirements.hasSpecialChar')}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <div>
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
          {t('passwordStep.confirmPassword')} <span className="text-red-500">{t('passwordStep.required')}</span>
        </label>
        <div className="relative">
          <input
            type={showConfirmPassword ? "text" : "password"}
            id="confirmPassword"
            value={formData.confirmPassword}
            onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
            placeholder={t('passwordStep.confirmPasswordPlaceholder')}
            className="w-full px-10 py-3 sm:py-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent shadow-sm text-sm sm:text-base"
            required
          />
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <svg width="16" height="16" className="sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="#9CA3AF" strokeWidth="2"/>
              <circle cx="12" cy="16" r="1" fill="#9CA3AF"/>
              <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="#9CA3AF" strokeWidth="2"/>
            </svg>
          </div>
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            {showConfirmPassword ? (
              <svg width="16" height="16" className="sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg width="16" height="16" className="sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 12C2 12 5.63636 6 12 6C18.3636 6 22 12 22 12C22 12 18.3636 18 12 18C5.63636 18 2 12 2 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 2L22 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
        </div>
      </div>
      
      {(error || errors.length > 0) && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 sm:p-4">
          {error && (
            <p className="text-red-600 text-xs sm:text-sm mb-2">{error}</p>
          )}
          {errors.length > 0 && (
            <div>
              {errors.map((err: string, index: number) => (
                <p key={index} className="text-red-600 text-xs sm:text-sm">{err}</p>
              ))}
            </div>
          )}
        </div>
      )}
      
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-red-600 text-white py-3 sm:py-4 px-4 rounded-md font-medium text-sm sm:text-base md:text-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? t('passwordStep.resetting') : t('passwordStep.resetPassword')}
      </button>
    </form>
  );

  // Render success step
  const renderSuccessStep = () => (
    <div className="text-center space-y-4 sm:space-y-6">
      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <svg className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          {t('success.title')}
        </h2>
        <p className="text-sm sm:text-base text-gray-600 px-2">
          {t('success.subtitle')}
        </p>
      </div>
      
      <button
        onClick={() => router.push(`/${locale}/giris`)}
        className="w-full bg-red-600 text-white py-3 sm:py-4 px-4 rounded-md font-medium text-sm sm:text-base md:text-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors shadow-sm"
      >
        {t('success.loginButton')}
      </button>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-white">
      {/* Sol taraf - Arka plan resmi ve içerik */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: 'url(/images/login-bg.jpg)',
            backgroundSize: 'cover'
          }}
        >
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative z-10 flex flex-col justify-between h-full p-12">
          {/* Logo */}
          <div>
            <h1 className="text-4xl font-bold text-white">{loginT('heroTitle')}</h1>
          </div>
          
          {/* Ana içerik */}
          <div className="max-w-md">
            <h2 className="text-5xl font-bold text-white mb-4 leading-tight">
              {loginT('heroSubtitle')}
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed">
              {loginT('heroDescription')}
            </p>
          </div>
          
          {/* Footer */}
          <div>
            <p className="text-sm text-white">{loginT('copyright')}</p>
          </div>
        </div>
      </div>
      
      {/* Sağ taraf - Şifremi unuttum formu */}
      <div className="flex-1 flex items-center justify-center p-3 sm:p-4 md:p-6 lg:p-8 bg-white">
        <div className="w-full max-w-md mx-auto">
          {/* Logo ve Başlık */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="flex justify-center mb-3 sm:mb-4 md:mb-6">
              <Image
                src="/images/logo.png"
                alt="Trink Emlak"
                width={300}
                height={135}
                className="h-16 sm:h-20 md:h-24 lg:h-30 w-auto"
                priority
              />
            </div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2">{t('title')}</h1>
            <p className="text-xs sm:text-sm md:text-base text-gray-600 px-2">{t('subtitle')}</p>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-6 sm:mb-8">
            <div className="flex items-center space-x-2 sm:space-x-4 overflow-x-auto w-full justify-center">
              {['phone', 'sms', 'password'].map((step, index) => (
                <div key={step} className="flex items-center flex-shrink-0">
                  <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium ${
                    currentStep === step 
                      ? 'bg-red-600 text-white' 
                      : ['phone', 'sms', 'password'].indexOf(currentStep) > index
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {['phone', 'sms', 'password'].indexOf(currentStep) > index ? (
                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      index + 1
                    )}
                  </div>
                  <span className={`ml-1 sm:ml-2 text-xs sm:text-sm font-medium hidden sm:inline ${
                    currentStep === step ? 'text-red-600' : 'text-gray-500'
                  }`}>
                    {t(`steps.${step}`)}
                  </span>
                  {index < 2 && (
                    <div className={`w-4 sm:w-8 h-0.5 ml-2 sm:ml-4 ${
                      ['phone', 'sms', 'password'].indexOf(currentStep) > index 
                        ? 'bg-green-600' 
                        : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step Content */}
          {currentStep === 'phone' && renderPhoneStep()}
          {currentStep === 'sms' && renderSmsStep()}
          {currentStep === 'password' && renderPasswordStep()}
          {currentStep === 'success' && renderSuccessStep()}
        </div>
      </div>
    </div>
  );
}
